import { getMatchStats, getPlayerStats } from "src/utils/matchDetailsUtils";

// Save rules for manual scoring. Which stats are editable (and their labels) comes from the
// shared per-sport definitions in matchDetailsUtils.
//   deriveTeamStats:   fields computed from the opponent on save, mirroring the feed sync
//   scoreKey:          team stat mirrored into Details.HomeTeamScore / AwayTeamScore (team win)
//   derivePlayerStats: player fields computed on save

const goalsAgainst = (team, opponent) => ({ GoalsAgainst: opponent.Goals ?? 0 });
const shutouts = (team, opponent) => ({ Shutouts: (opponent.Goals ?? 0) > 0 ? 0 : 1 });

const MANUAL_SCORING_RULES = {
    hockey: {
        deriveTeamStats: (team, opponent) => ({
            ...goalsAgainst(team, opponent),
            ...shutouts(team, opponent),
        }),
        scoreKey: "Goals",
    },
    soccer: {
        deriveTeamStats: (team, opponent) => ({
            ...goalsAgainst(team, opponent),
            ...shutouts(team, opponent),
        }),
        scoreKey: "Goals",
        // Goalkeepers (position 1) get a clean sheet when the opponent did not score
        derivePlayerStats: (player, team, opponent) => ({
            CleanSheet: (opponent.Goals ?? 0) <= 0 && Number(player.PlayerScoreablePosition) === 1 ? 1 : 0,
        }),
    },
    basketball: {
        deriveTeamStats: (team, opponent) => ({
            ...goalsAgainst(team, opponent),
            OpponentTurnovers: opponent.Turnovers ?? 0,
        }),
        scoreKey: "Points",
    },
    australian_football: {
        deriveTeamStats: goalsAgainst,
        scoreKey: "Goals",
    },
    rugby: {
        deriveTeamStats: (team, opponent) => ({ ScoreAgainst: opponent.Score ?? 0 }),
        scoreKey: "Score",
    },
    cricket: {
        deriveTeamStats: (team, opponent) => ({ RunsAgainst: opponent.Runs ?? 0 }),
        scoreKey: "Runs",
    },
    football: {
        deriveTeamStats: (team, opponent) => ({ PointsAllowed: opponent.Score ?? 0 }),
        scoreKey: "Score",
    },
};

export const getManualScoringConfig = (sportType) => ({
    ...(MANUAL_SCORING_RULES[sportType] || MANUAL_SCORING_RULES.hockey),
    teamStats: getMatchStats(sportType).filter((stat) => stat.editable),
    playerStats: getPlayerStats(sportType).filter((stat) => stat.editable),
});

const setStat = (entry, stat, value) => {
    entry[stat.key] = value;
    (stat.targets || []).forEach((target) => {
        entry[target] = value;
    });
};

// Applies edits to a copy of the fixture stats and recomputes derived fields.
// editedStats keys: "home-<TeamStat>", "away-<TeamStat>" or "<playerExternalRef>-<PlayerStat>".
export const buildManualScoringStats = ({
    sportType,
    stats,
    details,
    editedStats,
    homeScoreables,
    awayScoreables,
    teamScoreableIds = {},
}) => {
    const config = getManualScoringConfig(sportType);
    const updatedStats = JSON.parse(JSON.stringify(stats || []));
    const scoreablesBySide = { home: homeScoreables || [], away: awayScoreables || [] };
    const teamEntries = {};

    for (const side of ["home", "away"]) {
        const teamFeedId = details[side === "home" ? "HomeTeamFeedId" : "AwayTeamFeedId"];
        let teamEntry = updatedStats.find((t) => t.TeamFeedId === teamFeedId);
        if (!teamEntry) {
            teamEntry = { TeamFeedId: teamFeedId, Players: [] };
            config.teamStats.forEach((stat) => setStat(teamEntry, stat, 0));
            updatedStats.push(teamEntry);
        }
        teamEntry.TeamScoreableId = teamEntry.TeamScoreableId ?? teamScoreableIds[side] ?? null;
        teamEntry.Players = teamEntry.Players || [];
        teamEntries[side] = teamEntry;
    }

    for (const [editKey, rawValue] of Object.entries(editedStats)) {
        const value = Number(rawValue) || 0;
        const separatorIndex = editKey.lastIndexOf("-");
        const owner = editKey.slice(0, separatorIndex);
        const statKey = editKey.slice(separatorIndex + 1);

        if (owner === "home" || owner === "away") {
            const stat = config.teamStats.find((s) => s.key === statKey) || { key: statKey };
            setStat(teamEntries[owner], stat, value);
            continue;
        }

        const side = ["home", "away"].find((s) =>
            scoreablesBySide[s].some((p) => p.externalRef === owner)
        );
        if (!side) continue;
        const player = scoreablesBySide[side].find((p) => p.externalRef === owner);

        let playerEntry = teamEntries[side].Players.find((p) => p.PlayerFeedId === owner);
        if (!playerEntry) {
            playerEntry = { PlayerFeedId: owner };
            config.playerStats.forEach((stat) => setStat(playerEntry, stat, 0));
            teamEntries[side].Players.push(playerEntry);
        }
        playerEntry.PlayerName =
            playerEntry.PlayerName || `${player.firstname || ""} ${player.lastname || ""}`.trim();
        playerEntry.PlayerScoreableId = playerEntry.PlayerScoreableId ?? player.scoreableId;
        playerEntry.PlayerScoreablePosition = playerEntry.PlayerScoreablePosition ?? player.position;

        const stat = config.playerStats.find((s) => s.key === statKey) || { key: statKey };
        setStat(playerEntry, stat, value);
    }

    const sides = [["home", "away"], ["away", "home"]];
    const derived = sides.map(([side, other]) =>
        config.deriveTeamStats ? config.deriveTeamStats(teamEntries[side], teamEntries[other]) : {}
    );
    sides.forEach(([side, other], index) => {
        Object.assign(teamEntries[side], derived[index]);
        if (config.derivePlayerStats) {
            teamEntries[side].Players.forEach((player) =>
                Object.assign(player, config.derivePlayerStats(player, teamEntries[side], teamEntries[other]))
            );
        }
    });

    // Team win for most sports is scored from Details, so mirror the score stat when it was edited
    let updatedDetails = null;
    if (`home-${config.scoreKey}` in editedStats || `away-${config.scoreKey}` in editedStats) {
        updatedDetails = {
            ...details,
            HomeTeamScore: teamEntries.home[config.scoreKey] ?? 0,
            AwayTeamScore: teamEntries.away[config.scoreKey] ?? 0,
        };
    }

    return { stats: updatedStats, details: updatedDetails };
};
